import { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n';
import TranslatedText from './TranslatedText';
import axios from 'axios';
import { Calendar, MapPin, Droplet, Trash2, User, RefreshCw, AlertTriangle, Heart, Waves, Activity, Anchor, Camera, X, TrendingUp, FileText, Download, ClipboardList } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, LineChart, Line, XAxis, YAxis, CartesianGrid } from 'recharts';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';
import { LOGO_BASE64 } from '../assets/logoBase64';
import QuestionnaireList from './QuestionnaireList';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// CATEGORIE MATERIALI CON COLORI DISTINTI
const CATEGORY_COLORS = {
  generic: '#6B7280',           // Grigio (fallback per dati vecchi)
  plastica_monouso: '#3B82F6',  // Blu
  mozziconi_cotton: '#F97316',  // Arancione
  attrezzatura_pesca: '#EF4444', // Rosso
  polistirolo: '#FBBF24',       // Giallo
  microplastiche: '#8B5CF6',    // Viola
  vetro_ceramica: '#06B6D4',    // Cyan
  metalli: '#EC4899',           // Rosa/Magenta
  rifiuti_sanitari: '#14B8A6',  // Verde Acqua
  ingombranti: '#22C55E',       // Verde
  altro: '#78716C'              // Marrone/Grigio scuro
};

const CATEGORY_LABELS = {
  generic: 'Generico',
  plastica_monouso: 'Plastica Monouso',
  mozziconi_cotton: 'Mozziconi e Cotton Fioc',
  attrezzatura_pesca: 'Attrezzatura da Pesca',
  polistirolo: 'Polistirolo Espanso',
  microplastiche: 'Microplastiche',
  vetro_ceramica: 'Vetro e Ceramica',
  metalli: 'Metalli',
  rifiuti_sanitari: 'Rifiuti Sanitari',
  ingombranti: 'Rifiuti Ingombranti',
  altro: 'Altro'
};

// Immagini categorie SOS
const SOS_CATEGORY_IMAGES = {
  'Tartarughe': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/vro0bdj7_IMG_6704.jpeg',
  'Foche': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/h3e1bl6v_IMG_6705.jpeg',
  'Cetacei': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/siqdwpih_IMG_6697.jpeg',
  'Uccelli Marini': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/d721edbb_IMG_6702.jpeg',
  'Pesci': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/eud3kxo1_IMG_6703.jpeg',
  'Oggetti Grandi': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/9s5f4r60_IMG_6701.jpeg',
  'Reti e Cime': 'https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/pf77o4nv_IMG_6700.jpeg'
};

const ANIMAL_STATUS_LABELS = { vivo: 'Vivo', morto: 'Morto', non_so: 'Non determinato' };
const ANIMAL_LOCATION_LABELS = { acqua: 'In acqua', spiaggia: 'In spiaggia' };
const WOUNDS_LABELS = { si: 'Sì', no: 'No', non_visibili: 'Non visibili' };

const StoricoSection = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const [collections, setCollections] = useState([]);
  const [sosReports, setSosReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pieData, setPieData] = useState([]);
  const [lineData, setLineData] = useState([]);
  const [viewMode, setViewMode] = useState('all');
  const [dataType, setDataType] = useState('both');
  const [deleting, setDeleting] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [generatingSinglePdf, setGeneratingSinglePdf] = useState(null);
  const [enlargedPhoto, setEnlargedPhoto] = useState(null);
  const [updatingLocation, setUpdatingLocation] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  
  // Filtro data per drill-down
  const [selectedDate, setSelectedDate] = useState(null);
  
  // Ref per il grafico a torta (per PDF)
  const pieChartRef = useRef(null);

  // Carica profilo utente per il logo
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserProfile(res.data);
      } catch (e) {
        console.log('Profile not loaded');
      }
    };
    if (token) fetchProfile();
  }, [token]);

  // Funzione per aggiungere posizione GPS a una raccolta
  const addLocationToCollection = async (collectionId) => {
    if (!navigator.geolocation) {
      toast.error('GPS non disponibile');
      return;
    }
    
    setUpdatingLocation(collectionId);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await axios.put(
            `${BACKEND_URL}/api/collections/${collectionId}/location`,
            {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          toast.success('Posizione aggiunta!');
          
          // Aggiorna la raccolta nella lista
          setCollections(prev => prev.map(c => 
            c.id === collectionId 
              ? { ...c, location: { lat: position.coords.latitude, lng: position.coords.longitude } }
              : c
          ));
        } catch (error) {
          toast.error('Errore nell\'aggiornamento della posizione');
        } finally {
          setUpdatingLocation(null);
        }
      },
      (error) => {
        setUpdatingLocation(null);
        if (error.code === error.PERMISSION_DENIED) {
          toast.error('Permesso GPS negato');
        } else {
          toast.error('Impossibile ottenere la posizione');
        }
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  };

  // Verifica se una raccolta ha una posizione valida
  const hasValidLocation = (collection) => {
    const loc = collection.location;
    return loc && loc.lat && loc.lng && (loc.lat !== 0 || loc.lng !== 0);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const collectionsEndpoint = viewMode === 'all' ? '/api/collections/all' : '/api/collections';
      const collectionsRes = await axios.get(`${BACKEND_URL}${collectionsEndpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollections(collectionsRes.data);

      const sosEndpoint = viewMode === 'all' ? '/api/sos/all' : '/api/sos';
      const sosRes = await axios.get(`${BACKEND_URL}${sosEndpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSosReports(sosRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [token, viewMode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Processa dati per Line Chart (andamento temporale)
  useEffect(() => {
    if (collections.length > 0) {
      processLineData();
    } else {
      setLineData([]);
    }
  }, [collections]);

  // Processa dati per Pie Chart (filtrati o globali)
  useEffect(() => {
    if (collections.length > 0) {
      processPieData();
    } else {
      setPieData([]);
    }
  }, [collections, selectedDate]);

  const processLineData = () => {
    const byDate = {};

    collections.forEach(col => {
      const dateKey = new Date(col.timestamp).toLocaleDateString('it-IT', { 
        day: '2-digit', month: 'short' 
      });
      const fullDate = new Date(col.timestamp).toISOString().split('T')[0];
      
      if (!byDate[dateKey]) {
        byDate[dateKey] = { 
          name: dateKey, 
          kg: 0, 
          fullDate: fullDate,
          timestamp: new Date(col.timestamp).getTime()
        };
      }
      byDate[dateKey].kg += col.kg_collected || 0;
    });

    const sorted = Object.values(byDate)
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(-14)
      .map(item => ({
        ...item,
        kg: parseFloat(item.kg.toFixed(2))
      }));

    setLineData(sorted);
  };

  const processPieData = () => {
    const totals = {
      generic: 0, plastica_monouso: 0, mozziconi_cotton: 0, attrezzatura_pesca: 0,
      polistirolo: 0, microplastiche: 0, vetro_ceramica: 0, metalli: 0,
      rifiuti_sanitari: 0, ingombranti: 0, altro: 0
    };

    const filteredCollections = selectedDate 
      ? collections.filter(col => {
          const colDate = new Date(col.timestamp).toISOString().split('T')[0];
          return colDate === selectedDate;
        })
      : collections;

    filteredCollections.forEach(col => {
      if (col.categories) {
        Object.keys(totals).forEach(key => {
          totals[key] += col.categories[key] || 0;
        });
      } else {
        totals.generic += col.kg_collected || 0;
      }
    });

    const data = Object.entries(totals)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        name: CATEGORY_LABELS[key],
        value: parseFloat(value.toFixed(2)),
        color: CATEGORY_COLORS[key],
        key: key
      }));

    setPieData(data);
  };

  // ====== GENERAZIONE PDF DETTAGLIATO ======
  const generatePDF = async () => {
    setGeneratingPdf(true);
    toast.info('Generazione PDF in corso...');

    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // === LOGO (embedded base64) ===
      try {
        pdf.addImage(LOGO_BASE64, 'JPEG', margin, yPos, 35, 35);
      } catch (e) {
        console.error('Errore aggiunta logo:', e);
        pdf.setFillColor(6, 182, 212);
        pdf.roundedRect(margin, yPos, 35, 20, 3, 3, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('RESPIRO', margin + 3, yPos + 8);
        pdf.text('BLU', margin + 3, yPos + 15);
      }

      // Titolo a destra del logo
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Report Ufficiale ESG', pageWidth - margin, yPos + 12, { align: 'right' });

      // Data generazione
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      const today = new Date().toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
      pdf.text(`Generato il ${today}`, pageWidth - margin, yPos + 20, { align: 'right' });

      yPos += 45;

      // Linea separatrice
      pdf.setDrawColor(6, 182, 212);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      // === SEZIONE AUTORE DEL REPORT ===
      const latestCollection = collections[0];
      if (latestCollection) {
        const hasOrg = latestCollection.organization_name;
        const hasLogo = latestCollection.organization_logo;
        const isSeaCollection = latestCollection.is_sea_collection;
        const participantsCount = latestCollection.participants_count || 1;
        
        // Box info autore - altezza dinamica
        let authorBoxHeight = 35;
        if (hasOrg && hasLogo) authorBoxHeight = 45;
        
        pdf.setFillColor(250, 245, 255); // Viola chiaro
        pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, authorBoxHeight, 3, 3, 'F');
        
        let contentX = margin + 5;
        
        // Logo organizzazione se presente
        if (hasLogo) {
          try {
            pdf.addImage(latestCollection.organization_logo, 'JPEG', margin + 5, yPos + 3, 20, 20);
            contentX = margin + 30;
          } catch (e) {
            console.error('Errore aggiunta logo organizzazione:', e);
          }
        }
        
        // Titolo sezione
        pdf.setTextColor(139, 92, 246);
        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.text('REPORT GENERATO DA:', contentX, yPos + 8);
        
        // Nome volontario/organizzazione
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        const authorName = hasOrg ? latestCollection.organization_name : latestCollection.volunteer_name;
        pdf.text(authorName || 'Volontario', contentX, yPos + 16);
        
        // Tipo utente e specifica attività
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        
        let activityType = 'Volontario';
        if (latestCollection.user_type === 'peschereccio') activityType = 'Peschereccio (Fishing for Litter)';
        else if (latestCollection.user_type === 'azienda') activityType = 'Azienda';
        else if (latestCollection.user_type === 'ente') activityType = 'Ente Pubblico';
        else if (latestCollection.user_type === 'associazione') activityType = 'Associazione';
        else if (latestCollection.user_type === 'fondazione') activityType = 'Fondazione';
        
        pdf.text(`Tipo: ${activityType}`, contentX, yPos + 23);
        
        // Tipo raccolta (mare/terra)
        const collectionType = isSeaCollection ? 'Raccolta in mare' : 'Raccolta a terra';
        pdf.text(`Attività: ${collectionType}`, contentX + 50, yPos + 23);
        
        // Numero partecipanti
        if (participantsCount > 1) {
          pdf.setTextColor(16, 185, 129);
          pdf.setFont('helvetica', 'bold');
          pdf.text(`${participantsCount} partecipanti`, contentX, yPos + 30);
        }
        
        // Data e luogo sulla destra
        const latestDate = new Date(latestCollection.timestamp).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
        pdf.setTextColor(100, 100, 100);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.text(`Data: ${latestDate}`, pageWidth - margin - 50, yPos + 16, { align: 'left' });
        
        if (latestCollection.place) {
          pdf.text(`Luogo: ${latestCollection.place}`, pageWidth - margin - 50, yPos + 23, { align: 'left' });
        }
        
        yPos += authorBoxHeight + 10;
      }

      // === RIEPILOGO KG TOTALI ===
      const totalKg = pieData.reduce((sum, item) => sum + item.value, 0);
      const totalCO2 = (totalKg * 0.15).toFixed(2);
      const totalHours = collections.reduce((sum, c) => sum + (c.hours_spent || 0), 0);
      
      pdf.setFillColor(16, 185, 129, 20);
      pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 30, 3, 3, 'F');
      
      pdf.setTextColor(6, 182, 212);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('RIEPILOGO RACCOLTE', margin + 5, yPos + 8);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(24);
      pdf.text(`${totalKg.toFixed(1)} kg`, margin + 5, yPos + 22);
      
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Totale materiali raccolti (${collections.length} raccolte)`, margin + 50, yPos + 20);
      
      yPos += 40;

      // === GRAFICO A TORTA ===
      if (pieChartRef.current && pieData.length > 0) {
        try {
          const canvas = await html2canvas(pieChartRef.current, { 
            backgroundColor: '#ffffff',
            scale: 2
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = 75;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          pdf.setTextColor(0, 0, 0);
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.text('RIPARTIZIONE MATERIALI', margin, yPos);
          yPos += 5;
          
          pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, Math.min(imgHeight, 55));
          
          // Legenda accanto al grafico
          let legendY = yPos + 5;
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'normal');
          pieData.forEach((item) => {
            const hexColor = item.color.replace('#', '');
            const r = parseInt(hexColor.substr(0, 2), 16);
            const g = parseInt(hexColor.substr(2, 2), 16);
            const b = parseInt(hexColor.substr(4, 2), 16);
            
            pdf.setFillColor(r, g, b);
            pdf.circle(margin + imgWidth + 8, legendY, 2, 'F');
            
            pdf.setTextColor(0, 0, 0);
            const percentage = ((item.value / totalKg) * 100).toFixed(1);
            pdf.text(`${item.name}: ${item.value} kg (${percentage}%)`, margin + imgWidth + 13, legendY + 1);
            legendY += 5;
          });
          
          yPos += Math.min(imgHeight, 55) + 10;
        } catch (e) {
          console.error('Error capturing chart:', e);
        }
      }

      // === DETTAGLIO ESTESO MATERIALI ===
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('DETTAGLIO MATERIALI RACCOLTI', margin, yPos);
      yPos += 6;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pieData.forEach((item, idx) => {
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          yPos = margin;
        }
        const bgColor = idx % 2 === 0 ? 250 : 245;
        pdf.setFillColor(bgColor, bgColor, bgColor);
        pdf.rect(margin, yPos, pageWidth - 2 * margin, 5, 'F');
        
        pdf.setTextColor(80, 80, 80);
        pdf.text(`• ${item.name}`, margin + 2, yPos + 3.5);
        pdf.text(`${item.value} kg`, pageWidth - margin - 20, yPos + 3.5);
        yPos += 5;
      });
      yPos += 8;

      // === BLOCCO ESG ===
      if (yPos > pageHeight - 60) {
        pdf.addPage();
        yPos = margin;
      }

      pdf.setFillColor(139, 92, 246, 10);
      pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 3, 3, 'F');
      
      pdf.setTextColor(139, 92, 246);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('INDICATORI ESG', margin + 5, yPos + 8);
      
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      
      // E
      pdf.setTextColor(16, 185, 129);
      pdf.text('[E] Environmental:', margin + 5, yPos + 16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`${totalCO2} kg CO2eq evitata`, margin + 45, yPos + 16);
      
      // S
      pdf.setTextColor(59, 130, 246);
      pdf.text('[S] Social:', margin + 5, yPos + 23);
      pdf.setTextColor(0, 0, 0);
      pdf.text(`${totalHours} ore di volontariato`, margin + 45, yPos + 23);
      
      // G
      pdf.setTextColor(245, 158, 11);
      pdf.text('[G] Governance:', margin + 5, yPos + 30);
      pdf.setTextColor(0, 0, 0);
      pdf.text('100% Tracciabilità (GPS + Foto)', margin + 45, yPos + 30);
      
      yPos += 45;

      // === VALIDAZIONE: FOTO RACCOLTE ===
      const collectionsWithPhotos = collections.filter(c => c.photo_url);
      if (collectionsWithPhotos.length > 0) {
        // Titolo sezione
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('VALIDAZIONE FOTOGRAFICA', margin, yPos);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 100, 100);
        pdf.text(`(${collectionsWithPhotos.length} foto allegate)`, margin + 55, yPos);
        yPos += 8;

        // Aggiungi miniature foto (max 6 per pagina)
        const photoSize = 35;
        const photosPerRow = 4;
        const photoSpacing = 5;
        let photoX = margin;
        let photosInRow = 0;

        for (let i = 0; i < Math.min(collectionsWithPhotos.length, 8); i++) {
          const col = collectionsWithPhotos[i];
          
          // Controlla se serve una nuova pagina
          if (yPos > pageHeight - photoSize - 20) {
            pdf.addPage();
            yPos = margin;
            photoX = margin;
            photosInRow = 0;
          }

          // Nuova riga se necessario
          if (photosInRow >= photosPerRow) {
            yPos += photoSize + photoSpacing + 10;
            photoX = margin;
            photosInRow = 0;
          }

          try {
            const photoUrl = col.photo_url;
            if (photoUrl) {
              if (photoUrl.startsWith('data:image')) {
                // È già base64 - estrai formato
                const formatMatch = photoUrl.match(/data:image\/(\w+);base64,/);
                const format = formatMatch ? formatMatch[1].toUpperCase() : 'JPEG';
                const validFormat = ['JPEG', 'PNG', 'WEBP', 'JPG'].includes(format) ? format : 'JPEG';
                
                try {
                  pdf.addImage(photoUrl, validFormat === 'JPG' ? 'JPEG' : validFormat, photoX, yPos, photoSize, photoSize);
                } catch (imgErr) {
                  // Fallback: prova con JPEG generico
                  try {
                    pdf.addImage(photoUrl, 'JPEG', photoX, yPos, photoSize, photoSize);
                  } catch {
                    // Placeholder finale
                    pdf.setFillColor(230, 230, 230);
                    pdf.rect(photoX, yPos, photoSize, photoSize, 'F');
                    pdf.setFontSize(7);
                    pdf.setTextColor(150, 150, 150);
                    pdf.text('Foto', photoX + 13, yPos + 18);
                  }
                }
              } else {
                // URL esterno - placeholder (CORS issues)
                pdf.setFillColor(230, 230, 230);
                pdf.rect(photoX, yPos, photoSize, photoSize, 'F');
                pdf.setDrawColor(200, 200, 200);
                pdf.rect(photoX, yPos, photoSize, photoSize, 'S');
                pdf.setFontSize(7);
                pdf.setTextColor(150, 150, 150);
                pdf.text('Foto', photoX + 13, yPos + 18);
              }

              // Data e kg sotto la foto
              const photoDate = new Date(col.timestamp).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
              pdf.setFontSize(6);
              pdf.setTextColor(100, 100, 100);
              pdf.text(photoDate, photoX + photoSize/2, yPos + photoSize + 3, { align: 'center' });
              pdf.text(`${col.kg_collected} kg`, photoX + photoSize/2, yPos + photoSize + 6, { align: 'center' });
            }
          } catch (photoError) {
            console.error('Errore caricamento foto:', photoError);
            pdf.setFillColor(220, 220, 220);
            pdf.rect(photoX, yPos, photoSize, photoSize, 'F');
          }

          photoX += photoSize + photoSpacing;
          photosInRow++;
        }

        // Nota se ci sono più foto
        if (collectionsWithPhotos.length > 8) {
          yPos += photoSize + 15;
          pdf.setFontSize(8);
          pdf.setTextColor(100, 100, 100);
          pdf.text(`... e altre ${collectionsWithPhotos.length - 8} foto nelle raccolte`, margin, yPos);
        }

        yPos += photoSize + 20;
      }

      // === NUOVA PAGINA PER SPIEGAZIONE ESG ===
      pdf.addPage();
      yPos = margin;

      pdf.setTextColor(139, 92, 246);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('COSA SONO I PARAMETRI ESG', margin, yPos);
      yPos += 8;

      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      
      const esgText = [
        'I criteri ESG (Environmental, Social, Governance) sono gli standard globali utilizzati per valutare',
        "l'impatto di un'attività sulla sostenibilità.",
        '',
        'Environmental (Ambiente): Indica il beneficio diretto per il pianeta. In questo report, è calcolato',
        'in base alla plastica rimossa che, se degradata, rilascerebbe gas serra.',
        '',
        "Social (Sociale): Misura l'impegno civico e il valore del tempo dedicato dai volontari alla tutela",
        'del bene comune.',
        '',
        'Governance (Trasparenza): Garantisce che il dato sia reale, verificato tramite posizione GPS e',
        'documentazione fotografica incontrovertibile.',
        '',
        'Finalità dei dati: Questi valori servono come evidenza digitale oggettiva per supportare i bilanci',
        'di sostenibilità aziendali (DNF) e permettono alle associazioni ambientaliste di monitorare e',
        'certificare il lavoro svolto dai volontari sul territorio.'
      ];
      
      esgText.forEach(line => {
        pdf.text(line, margin, yPos);
        yPos += 5;
      });

      // === TABELLA RIASSUNTIVA ===
      yPos += 10;
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text('TABELLA RIASSUNTIVA RACCOLTE', margin, yPos);
      yPos += 7;

      // Header tabella
      pdf.setFillColor(6, 182, 212);
      pdf.rect(margin, yPos, pageWidth - 2 * margin, 8, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Data', margin + 2, yPos + 5);
      pdf.text('Volontario', margin + 25, yPos + 5);
      pdf.text('Luogo', margin + 60, yPos + 5);
      pdf.text('Kg', margin + 110, yPos + 5);
      pdf.text('CO2eq', margin + 125, yPos + 5);
      pdf.text('Ore', margin + 145, yPos + 5);
      pdf.text('Foto', margin + 160, yPos + 5);
      yPos += 8;

      // Righe tabella
      pdf.setFont('helvetica', 'normal');
      collections.slice(0, 25).forEach((col, index) => {
        if (yPos > pageHeight - 25) {
          pdf.addPage();
          yPos = margin;
        }
        
        const bgColor = index % 2 === 0 ? 250 : 240;
        pdf.setFillColor(bgColor, bgColor, bgColor);
        pdf.rect(margin, yPos, pageWidth - 2 * margin, 5, 'F');
        
        pdf.setTextColor(50, 50, 50);
        const date = new Date(col.timestamp).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
        pdf.text(date, margin + 2, yPos + 3.5);
        pdf.text((col.volunteer_name || 'Volontario').substring(0, 18), margin + 25, yPos + 3.5);
        pdf.text((col.place || '-').substring(0, 25), margin + 60, yPos + 3.5);
        pdf.text(`${col.kg_collected}`, margin + 110, yPos + 3.5);
        pdf.text(`${(col.kg_collected * 0.15).toFixed(1)}`, margin + 125, yPos + 3.5);
        pdf.text(`${col.hours_spent || 0}`, margin + 145, yPos + 3.5);
        pdf.text(col.photo_url ? 'Sì' : 'No', margin + 160, yPos + 3.5);
        yPos += 5;
      });

      // === FOOTER OBBLIGATORIO ===
      const addFooter = (pdfDoc) => {
        const footerY = pageHeight - 10;
        pdfDoc.setDrawColor(200, 200, 200);
        pdfDoc.line(margin, footerY - 3, pageWidth - margin, footerY - 3);
        
        pdfDoc.setFontSize(6);
        pdfDoc.setTextColor(100, 100, 100);
        pdfDoc.setFont('helvetica', 'normal');
        const footerText = 'Applicazione ideata, generata e gestita da Massimo Marchiori — P. IVA 04056480231 — Via P. Zecchinato 7, 37136 Verona (VR). Tutti i diritti riservati.';
        pdfDoc.text(footerText, pageWidth / 2, footerY, { align: 'center', maxWidth: pageWidth - 2 * margin });
      };

      // Aggiungi footer a tutte le pagine
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        addFooter(pdf);
      }

      // Salva PDF
      pdf.save('RespiroBlu_Report_ESG.pdf');
      toast.success('PDF generato con successo!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Errore nella generazione del PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  // Reset filtro
  const resetFilter = () => {
    setSelectedDate(null);
    toast.info('Visualizzazione globale ripristinata');
  };

  const handleDeleteCollection = async (collectionId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa raccolta?')) return;
    
    setDeleting(collectionId);
    try {
      await axios.delete(`${BACKEND_URL}/api/collections/${collectionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCollections(prev => prev.filter(c => c.id !== collectionId));
      toast.success('Raccolta eliminata');
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Errore nella cancellazione');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteSOS = async (sosId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa segnalazione SOS?')) return;
    
    setDeleting(sosId);
    try {
      await axios.delete(`${BACKEND_URL}/api/sos/${sosId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSosReports(prev => prev.filter(s => s.id !== sosId));
      toast.success('Segnalazione SOS eliminata');
    } catch (error) {
      console.error('Error deleting SOS:', error);
      toast.error('Errore nella cancellazione');
    } finally {
      setDeleting(null);
    }
  };

  // Genera PDF per singola raccolta
  const generateSingleCollectionPDF = async (collection) => {
    setGeneratingSinglePdf(collection.id);
    
    try {
      const pdf = new jsPDF();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = margin;

      // Pre-carica logo organizzazione se disponibile
      const orgLogoUrl = userProfile?.organization_logo || collection.organization_logo;
      let orgLogoDataUrl = null;
      
      if (orgLogoUrl) {
        try {
          // Converti URL in base64 tramite canvas
          const loadImage = (url) => {
            return new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'Anonymous';
              img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve({
                  dataUrl: canvas.toDataURL('image/png'),
                  width: img.width,
                  height: img.height
                });
              };
              img.onerror = () => reject(new Error('Image load failed'));
              img.src = url;
            });
          };
          
          const logoData = await loadImage(orgLogoUrl);
          orgLogoDataUrl = logoData;
        } catch (e) {
          console.log('Logo org pre-load failed:', e);
        }
      }

      // Genera QR code con ID raccolta
      const qrUrl = `https://www.respiroblu.it/raccolta/${collection.id}`;
      let qrDataUrl = null;
      try {
        qrDataUrl = await QRCode.toDataURL(qrUrl, { 
          width: 100, 
          margin: 1,
          color: { dark: '#06B6D4', light: '#ffffff' }
        });
      } catch (e) {
        console.error('Error generating QR:', e);
      }

      // Header con logo Respiro Blu a sinistra
      try {
        pdf.addImage(LOGO_BASE64, 'JPEG', margin, yPos, 25, 25);
      } catch (e) {
        pdf.setFillColor(6, 182, 212);
        pdf.roundedRect(margin, yPos, 25, 25, 3, 3, 'F');
      }

      // Logo organizzazione al centro (se disponibile)
      if (orgLogoDataUrl) {
        try {
          // Calcola dimensioni proporzionali (max 30x25)
          const maxW = 30, maxH = 25;
          let w = orgLogoDataUrl.width, h = orgLogoDataUrl.height;
          const ratio = Math.min(maxW / w, maxH / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
          
          // Centra il logo
          const centerX = (pageWidth - w) / 2;
          pdf.addImage(orgLogoDataUrl.dataUrl, 'PNG', centerX, yPos, w, h);
        } catch (e) {
          console.log('Logo org insert failed:', e);
        }
      }

      // QR code in alto a destra
      if (qrDataUrl) {
        pdf.addImage(qrDataUrl, 'PNG', pageWidth - 40, yPos, 25, 25);
      }

      // Titolo
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Certificato Raccolta Rifiuti', margin + 32, yPos + 10);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      const collectionDate = new Date(collection.timestamp).toLocaleDateString('it-IT', { 
        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' 
      });
      pdf.text(`Data: ${collectionDate}`, margin + 32, yPos + 18);
      
      yPos += 40;
      
      // Linea separatrice
      pdf.setDrawColor(6, 182, 212);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      // Info volontario
      pdf.setFillColor(240, 249, 255);
      pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 25, 3, 3, 'F');
      
      pdf.setTextColor(6, 182, 212);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.text('VOLONTARIO:', margin + 5, yPos + 8);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(12);
      pdf.text(collection.volunteer_name || 'Volontario', margin + 5, yPos + 18);
      
      if (collection.organization_name) {
        pdf.setFontSize(9);
        pdf.setTextColor(100, 100, 100);
        pdf.text(collection.organization_name, margin + 80, yPos + 18);
      }
      
      yPos += 35;

      // Riepilogo KG
      pdf.setFillColor(16, 185, 129, 20);
      pdf.roundedRect(margin, yPos, pageWidth - 2 * margin, 35, 3, 3, 'F');
      
      pdf.setTextColor(16, 185, 129);
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.text('TOTALE RACCOLTO', margin + 5, yPos + 12);
      
      pdf.setTextColor(0, 0, 0);
      pdf.setFontSize(28);
      pdf.text(`${collection.kg_collected} kg`, margin + 5, yPos + 28);
      
      // CO2
      pdf.setFontSize(10);
      pdf.setTextColor(16, 185, 129);
      pdf.text(`CO₂ evitata: ${(collection.kg_collected * 0.15).toFixed(2)} kg`, margin + 70, yPos + 28);
      
      yPos += 45;

      // Dettaglio materiali
      if (collection.categories) {
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('DETTAGLIO MATERIALI', margin, yPos);
        yPos += 8;
        
        const entries = Object.entries(collection.categories).filter(([_, val]) => val > 0);
        entries.forEach(([key, value]) => {
          const label = CATEGORY_LABELS[key] || key;
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(80, 80, 80);
          pdf.text(`• ${label}: ${value} kg`, margin + 5, yPos);
          yPos += 6;
        });
        yPos += 5;
      }

      // Luogo se presente
      if (collection.place && collection.place !== 'Non specificato') {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('LUOGO:', margin, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(collection.place, margin + 20, yPos);
        yPos += 10;
      }

      // Ore volontariato se presenti
      if (collection.hours_spent > 0) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('ORE VOLONTARIATO:', margin, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${collection.hours_spent}h`, margin + 50, yPos);
        yPos += 10;
      }

      // Numero partecipanti
      if (collection.participants_count > 1) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.text('PARTECIPANTI:', margin, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${collection.participants_count} persone`, margin + 40, yPos);
        yPos += 10;
      }

      // Foto se presente
      if (collection.photo_url) {
        // Controlla se c'è spazio nella pagina (footer a 15 dal fondo)
        const pageHeight = pdf.internal.pageSize.getHeight();
        const footerY = pageHeight - 20;
        const photoHeight = 50;
        
        // Se non c'è abbastanza spazio, aggiungi nuova pagina
        if (yPos + photoHeight + 20 > footerY) {
          pdf.addPage();
          yPos = 20;
        }
        
        yPos += 5;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(0, 0, 0);
        pdf.text('FOTO RACCOLTA:', margin, yPos);
        yPos += 5;
        
        try {
          // Carica la foto tramite canvas per evitare CORS
          const loadImage = (url) => {
            return new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'Anonymous';
              img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve({
                  dataUrl: canvas.toDataURL('image/jpeg', 0.8),
                  width: img.width,
                  height: img.height
                });
              };
              img.onerror = () => reject(new Error('Image load failed'));
              img.src = url;
            });
          };
          
          const photoData = await loadImage(collection.photo_url);
          
          // Calcola dimensioni proporzionali (max 80x50)
          const maxW = 80, maxH = 50;
          let w = photoData.width, h = photoData.height;
          const ratio = Math.min(maxW / w, maxH / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
          
          pdf.addImage(photoData.dataUrl, 'JPEG', margin, yPos, w, h);
          yPos += h + 10;
        } catch (e) {
          console.log('Foto non caricabile nel PDF:', e);
          pdf.setFillColor(230, 230, 230);
          pdf.rect(margin, yPos, 60, 40, 'F');
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text('Foto non disponibile', margin + 10, yPos + 20);
          yPos += 50;
        }
      }

      // Footer
      const footerY = pdf.internal.pageSize.getHeight() - 15;
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      pdf.setFont('helvetica', 'normal');
      pdf.text('Respiro Blu — Massimo Marchiori — P. IVA 04056480231 — Verona', pageWidth / 2, footerY, { align: 'center' });

      // Salva
      const fileName = `RespiroBlu_Raccolta_${new Date(collection.timestamp).toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      toast.success('PDF raccolta generato con QR code!');
    } catch (error) {
      console.error('Error generating single PDF:', error);
      toast.error('Errore nella generazione del PDF');
    } finally {
      setGeneratingSinglePdf(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getAllItems = () => {
    let items = [];
    
    if (dataType === 'both' || dataType === 'collections') {
      items = [...items, ...collections.map(c => ({ ...c, type: 'collection' }))];
    }
    if (dataType === 'both' || dataType === 'sos') {
      items = [...items, ...sosReports.map(s => ({ ...s, type: 'sos' }))];
    }
    
    return items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  // Custom tooltip per PieChart - Mostra % e Kg
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const total = pieData.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((data.value / total) * 100).toFixed(1);
      
      return (
        <div className="bg-slate-900 border-2 rounded-lg p-4 shadow-xl" style={{ borderColor: data.payload.color }}>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: data.payload.color }} />
            <p className="font-bold text-white text-base">{data.name}</p>
          </div>
          <div className="space-y-1">
            <p className="text-2xl font-bold" style={{ color: data.payload.color }}>{data.value} kg</p>
            <p className="text-slate-300 text-sm font-medium">{percentage}% del totale</p>
          </div>
        </div>
      );
    }
    return null;
  };

  // Custom tooltip per LineChart
  const CustomLineTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const isSelected = selectedDate === payload[0].payload.fullDate;
      return (
        <div className={`bg-slate-900 border rounded-lg p-3 shadow-lg ${isSelected ? 'border-emerald-500' : 'border-cyan-500/50'}`}>
          <p className="font-semibold text-white mb-1">{label}</p>
          <p className="text-cyan-400 text-lg font-bold">{payload[0].value} kg</p>
          <p className="text-slate-500 text-xs mt-1">
            {isSelected ? 'Clicca per rimuovere filtro' : 'Clicca per filtrare la torta'}
          </p>
        </div>
      );
    }
    return null;
  };

  // Custom label per PieChart - Mostra % e Kg sulle fette
  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, value, name }) => {
    // Non mostrare label per fette molto piccole (< 5%)
    if (percent < 0.05) return null;
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor="middle" 
        dominantBaseline="central"
        style={{ 
          fontSize: percent > 0.15 ? '11px' : '9px', 
          fontWeight: 'bold', 
          textShadow: '1px 1px 3px rgba(0,0,0,0.9)' 
        }}
      >
        <tspan x={x} dy="-0.4em">{`${(percent * 100).toFixed(0)}%`}</tspan>
        {percent > 0.1 && <tspan x={x} dy="1.2em" style={{ fontSize: '9px' }}>{`${value} kg`}</tspan>}
      </text>
    );
  };

  // Custom dot per LineChart
  const CustomDot = (props) => {
    const { cx, cy, payload } = props;
    const isSelected = selectedDate === payload.fullDate;
    
    return (
      <circle 
        cx={cx} 
        cy={cy} 
        r={isSelected ? 8 : 5} 
        fill={isSelected ? '#10B981' : '#06B6D4'} 
        stroke={isSelected ? '#10B981' : '#fff'}
        strokeWidth={2}
        style={{ cursor: 'pointer' }}
      />
    );
  };

  const totalKg = pieData.reduce((sum, item) => sum + item.value, 0);
  const allItems = getAllItems();

  const selectedDateDisplay = selectedDate 
    ? new Date(selectedDate).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_respiro-dashboard/artifacts/likcbe5b_IMG_6596.jpeg" 
            alt="Respiro Blu Logo" 
            className="w-32 h-32 rounded-2xl object-cover shadow-[0_0_30px_rgba(6,182,212,0.3)]"
          />
        </div>
        <h2 className="text-4xl md:text-5xl font-bold text-white text-center" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
          <TranslatedText text="STORICO ATTIVITÀ" />
        </h2>
        <p className="text-slate-400 mt-2 text-center">
          <TranslatedText text={viewMode === 'all' ? 'Attività di tutti i volontari' : 'Le tue attività'} />
        </p>
      </div>

      {/* Toggle view mode e data type */}
      <div className="flex flex-col gap-3 mb-6">
        <div className="flex justify-center gap-2 flex-wrap">
          <button
            onClick={() => setViewMode('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'all' ? 'bg-cyan-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            <TranslatedText text="Tutti i Volontari" />
          </button>
          <button
            onClick={() => setViewMode('mine')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              viewMode === 'mine' ? 'bg-cyan-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Solo le Mie
          </button>
          <button
            onClick={fetchData}
            className="px-3 py-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-all"
            title="Aggiorna"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {/* PULSANTE GENERA PDF */}
          <button
            onClick={generatePDF}
            disabled={generatingPdf || collections.length === 0}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Genera Report PDF"
          >
            {generatingPdf ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <FileText className="w-4 h-4" />
            )}
            Genera Report PDF
          </button>
        </div>

        <div className="flex justify-center gap-2">
          <button
            onClick={() => setDataType('both')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
              dataType === 'both' ? 'bg-slate-700 text-white' : 'bg-slate-800/50 text-slate-500 hover:text-white'
            }`}
          >
            Tutto
          </button>
          <button
            onClick={() => setDataType('collections')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
              dataType === 'collections' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-slate-800/50 text-slate-500 hover:text-cyan-400'
            }`}
          >
            <Droplet className="w-3 h-3" />
            Raccolte ({collections.length})
          </button>
          <button
            onClick={() => setDataType('sos')}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
              dataType === 'sos' ? 'bg-red-500/20 text-red-400' : 'bg-slate-800/50 text-slate-500 hover:text-red-400'
            }`}
          >
            <AlertTriangle className="w-3 h-3" />
            SOS ({sosReports.length})
          </button>
        </div>
      </div>

      {/* QUESTIONARI SCUOLE (solo admin) */}
      <div className="mb-6 bg-slate-900/60 border border-violet-500/30 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-4">
          <ClipboardList className="w-5 h-5 text-violet-400" />
          <h3 className="text-lg font-semibold text-white">Questionari Scuole</h3>
        </div>
        <QuestionnaireList />
      </div>

      {/* GRAFICI (solo per raccolte) */}
      {(dataType === 'both' || dataType === 'collections') && collections.length > 0 && (
        <>
          {/* GRAFICO A TORTA */}
          {pieData.length > 0 && (
            <div ref={pieChartRef} className="bg-slate-900/60 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-6 mb-6">
              <div className="text-center mb-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-white">
                    {selectedDate ? `Ripartizione del ${selectedDateDisplay}` : 'Ripartizione Globale Materiali'}
                  </h3>
                  {selectedDate && (
                    <button
                      onClick={resetFilter}
                      className="px-3 py-1 bg-red-500/20 text-red-400 text-xs rounded-lg hover:bg-red-500/30 transition-all flex items-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Reset Filtro
                    </button>
                  )}
                </div>
                <p className="text-cyan-400 text-2xl font-bold">{totalKg.toFixed(1)} kg {selectedDate ? 'in questo giorno' : 'totali'}</p>
              </div>
              
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={100}
                    innerRadius={40}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} stroke={entry.color} strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomPieTooltip />} />
                  <Legend 
                    layout="horizontal"
                    align="center"
                    verticalAlign="bottom"
                    formatter={(value) => <span className="text-slate-300 text-xs">{value}</span>}
                    iconType="circle"
                    iconSize={10}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* GRAFICO LINEARE (ANDAMENTO TEMPORALE) */}
          {lineData.length > 0 && (
            <div className="bg-slate-900/60 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-cyan-400" />
                  <h3 className="text-lg font-semibold text-white">Andamento Temporale</h3>
                </div>
                <p className="text-slate-400 text-sm">
                  Clicca su un punto per filtrare la torta
                </p>
              </div>
              
              <ResponsiveContainer width="100%" height={220}>
                <LineChart 
                  data={lineData} 
                  style={{ cursor: 'pointer' }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#94a3b8" 
                    style={{ fontSize: '11px' }}
                    angle={-30}
                    textAnchor="end"
                    height={50}
                  />
                  <YAxis 
                    stroke="#94a3b8" 
                    style={{ fontSize: '12px' }}
                    tickFormatter={(value) => `${value} kg`}
                  />
                  <Tooltip content={<CustomLineTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="kg" 
                    stroke="#06B6D4" 
                    strokeWidth={3}
                    dot={<CustomDot />}
                    activeDot={{ 
                      r: 10, 
                      fill: '#10B981', 
                      stroke: '#fff', 
                      strokeWidth: 2,
                      onClick: (e, payload) => {
                        if (payload && payload.payload) {
                          const clickedDate = payload.payload.fullDate;
                          if (selectedDate === clickedDate) {
                            setSelectedDate(null);
                            toast.info('Visualizzazione globale ripristinata');
                          } else {
                            setSelectedDate(clickedDate);
                            toast.success(`Filtro applicato: ${payload.payload.name}`);
                          }
                        }
                      }
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>

              {/* Indicatore filtro attivo */}
              {selectedDate && (
                <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-emerald-400 text-sm font-medium">
                      Filtro attivo: {selectedDateDisplay}
                    </span>
                  </div>
                  <button
                    onClick={resetFilter}
                    className="text-emerald-400 hover:text-emerald-300 text-sm underline"
                  >
                    Rimuovi filtro
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : allItems.length === 0 ? (
        <div className="text-center py-12 bg-slate-900/60 border border-white/10 rounded-2xl">
          <Droplet className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400 text-lg">Nessuna attività ancora</p>
        </div>
      ) : (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">
            Dettaglio Attività ({allItems.length})
          </h3>
          
          {allItems.map((item) => (
            item.type === 'collection' ? (
              /* CARD RACCOLTA */
              <div 
                key={`col-${item.id}`}
                className="bg-slate-900/60 backdrop-blur-xl border border-cyan-500/30 rounded-2xl p-6 hover:border-cyan-500/50 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs font-bold rounded uppercase">
                        Raccolta
                      </span>
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3 text-slate-400" />
                        <span className="text-slate-400 text-sm">{item.volunteer_name || 'Volontario'}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <div className="flex items-center gap-2 px-3 py-1 bg-cyan-500/20 rounded-lg">
                        <Droplet className="w-4 h-4 text-cyan-400" />
                        <span className="text-cyan-400 font-bold">{item.kg_collected} kg</span>
                      </div>
                      <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/20 rounded-lg">
                        <span className="text-emerald-400 font-bold text-xs">CO₂: {(item.kg_collected * 0.15).toFixed(2)} kg</span>
                      </div>
                      {item.hours_spent > 0 && (
                        <div className="flex items-center gap-2 px-3 py-1 bg-blue-500/20 rounded-lg">
                          <span className="text-blue-400 font-bold text-xs">{item.hours_spent}h volontariato</span>
                        </div>
                      )}
                    </div>

                    {item.categories && (
                      <div className="flex flex-wrap gap-2 mb-3">
                        {Object.entries(item.categories).map(([key, value]) => (
                          value > 0 && (
                            <span key={key} className="text-xs px-2 py-1 rounded"
                              style={{ backgroundColor: `${CATEGORY_COLORS[key] || '#64748B'}20`, color: CATEGORY_COLORS[key] || '#64748B' }}>
                              {CATEGORY_LABELS[key] || key}: {value} kg
                            </span>
                          )
                        ))}
                      </div>
                    )}

                    {item.place && item.place !== 'Non specificato' && (
                      <div className="flex items-start gap-2 text-slate-300 text-sm mb-2">
                        <MapPin className="w-4 h-4 text-emerald-400 mt-0.5" />
                        <span>{item.place}</span>
                      </div>
                    )}

                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(item.timestamp)}</span>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    {/* Pulsante per aggiungere posizione GPS se mancante */}
                    {item.user_id === user?.uid && !hasValidLocation(item) && (
                      <button
                        type="button"
                        onClick={() => addLocationToCollection(item.id)}
                        disabled={updatingLocation === item.id}
                        className="p-2 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 rounded-lg transition-all disabled:opacity-50"
                        title="Aggiungi posizione GPS"
                      >
                        {updatingLocation === item.id ? (
                          <div className="w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <MapPin className="w-5 h-5" />
                        )}
                      </button>
                    )}
                    
                    {/* Pulsante PDF singola raccolta */}
                    <button
                      type="button"
                      onClick={() => generateSingleCollectionPDF(item)}
                      disabled={generatingSinglePdf === item.id}
                      className="p-2 text-slate-500 hover:text-cyan-400 hover:bg-cyan-500/10 rounded-lg transition-all disabled:opacity-50"
                      title="Scarica PDF raccolta"
                    >
                      {generatingSinglePdf === item.id ? (
                        <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <FileText className="w-5 h-5" />
                      )}
                    </button>
                    
                    {item.user_id === user?.uid && (
                      <button
                        type="button"
                        onClick={() => handleDeleteCollection(item.id)}
                        disabled={deleting === item.id}
                        className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-50"
                        title="Elimina raccolta"
                      >
                        {deleting === item.id ? (
                          <div className="w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-5 h-5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              /* CARD SOS */
              <div 
                key={`sos-${item.id}`}
                className="bg-slate-900/60 backdrop-blur-xl border border-red-500/30 rounded-2xl p-6 hover:border-red-500/50 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs font-bold rounded uppercase flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        SOS
                      </span>
                      <div className="flex items-center gap-1">
                        <User className="w-3 h-3 text-slate-400" />
                        <span className="text-slate-400 text-sm">{item.volunteer_name || 'Volontario'}</span>
                      </div>
                      {item.photo_url && (
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded flex items-center gap-1">
                          <Camera className="w-3 h-3" />
                          Foto
                        </span>
                      )}
                    </div>

                    {/* Foto + Categoria */}
                    <div className="flex items-start gap-4 mb-3">
                      {item.photo_url && (
                        <img 
                          src={item.photo_url} 
                          alt="Foto emergenza" 
                          className="w-24 h-24 object-cover rounded-lg border border-red-500/30 cursor-pointer hover:opacity-80 hover:ring-2 hover:ring-red-400 transition-all"
                          onClick={() => setEnlargedPhoto(item.photo_url)}
                          title="Clicca per ingrandire"
                        />
                      )}
                      <div className="flex items-center gap-3">
                        {SOS_CATEGORY_IMAGES[item.category] && (
                          <img 
                            src={SOS_CATEGORY_IMAGES[item.category]} 
                            alt={item.category} 
                            className="w-12 h-12 object-contain"
                          />
                        )}
                        <span className="text-red-400 font-bold text-lg">{item.category}</span>
                      </div>
                    </div>

                    {/* Dettagli animale */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {item.animal_status && (
                        <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                          item.animal_status === 'vivo' ? 'bg-emerald-500/20 text-emerald-400' :
                          item.animal_status === 'morto' ? 'bg-red-500/20 text-red-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          <Heart className="w-3 h-3" />
                          {ANIMAL_STATUS_LABELS[item.animal_status]}
                        </span>
                      )}
                      {item.animal_location && (
                        <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                          item.animal_location === 'acqua' ? 'bg-cyan-500/20 text-cyan-400' : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          <Waves className="w-3 h-3" />
                          {ANIMAL_LOCATION_LABELS[item.animal_location]}
                        </span>
                      )}
                      {item.has_wounds && (
                        <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                          item.has_wounds === 'si' ? 'bg-red-500/20 text-red-400' :
                          item.has_wounds === 'no' ? 'bg-emerald-500/20 text-emerald-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          <Activity className="w-3 h-3" />
                          Ferite: {WOUNDS_LABELS[item.has_wounds]}
                        </span>
                      )}
                      {item.has_nets !== null && item.has_nets !== undefined && (
                        <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                          item.has_nets ? 'bg-red-500/20 text-red-400' : 'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          <Anchor className="w-3 h-3" />
                          Reti: {item.has_nets ? 'Sì' : 'No'}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(item.timestamp)}</span>
                    </div>
                  </div>

                  {item.user_id === user?.uid && (
                    <button
                      type="button"
                      onClick={() => handleDeleteSOS(item.id)}
                      disabled={deleting === item.id}
                      className="p-2 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-50"
                      title="Elimina segnalazione"
                    >
                      {deleting === item.id ? (
                        <div className="w-5 h-5 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Modal foto ingrandita */}
      {enlargedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setEnlargedPhoto(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-slate-800/80 hover:bg-slate-700 rounded-full text-white"
            onClick={() => setEnlargedPhoto(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={enlargedPhoto} 
            alt="Foto ingrandita" 
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default StoricoSection;
